import React, { useEffect, useRef, useState } from 'react';
import { Image, StyleSheet, ImageSourcePropType, View, Text } from 'react-native';

import Game from '../shell/Game';
import ImageClass from '../shell/Image';
import { isMobile } from '../shell/utils/platformCheck';
import { setSeenImage } from '../store/slices/AdvanceSlice';
import { TimageINT, TimageINTExtend } from '../store/slices/gameObjectSlice';
import { GameNames } from '../store/slices/GameTypes';
import { store } from '../store/store';
import { parseRequire } from '../utils/parseImageUrl';
import { TImagePartial } from './TImagePartial';
import { produce } from 'immer';
import { WaitForLoadImage } from '../shell/ImageHandler/WaitForLoadImage';

export interface TImageINT {
    image: TimageINT,
    onFinishedPainting?: (image?: any) => void;
}

export const Timage = React.memo(({ image, onFinishedPainting }: TImageINT) => {

    const previousImage = useRef<TimageINT>();
    const [imageToDisplayArr, setImageToDisplayArr] = useState<React.ReactElement[]>([]);
    const mainKey = useRef<string>();
    const imageMain = useRef<TimageINT | undefined>();

    const [imageObject, setImageObject] = useState<{ url: ImageSourcePropType | null, partials: React.ReactElement[]; }>({ url: null, partials: [] });
    const tempPartials: React.ReactElement<any, string | React.JSXElementConstructor<any>>[] = [];
    const [triggerLooping, setTriggerLooping] = useState(false);
    const loopingRef = useRef<NodeJS.Timeout>();
    const setParentFlag = useRef(false);


    useEffect(() => {
        setParentFlag.current = false;
        let _imageMain: TimageINT = { ...image, ...ImageClass._globalImages.get(image.key) };
        imageMain.current = _imageMain;
        if (Game.rollback && imageMain.current) {
            imageMain.current = produce(imageMain.current, draft => {
                if (!draft?.options) {
                    draft!.options = {};
                }

                draft.options!.duration = 0;
                draft.options!.wait = 0;
                if (draft?.partialsToDisplay?.[draft.partialsToDisplay?.length - 1]) {
                    draft.partialsToDisplay[draft.partialsToDisplay?.length - 1] = produce(draft?.partialsToDisplay?.[draft.partialsToDisplay?.length - 1], draft => {
                        if (!draft.options) {
                            draft.options = {};
                        }
                        draft.options!.duration = 0;
                        draft.options!.wait = 0;
                    });
                }
            });
        }


        setImage(imageMain.current!);
        if (!Game.isReplayRunning && imageMain.current?.url) {
            store.dispatch(setSeenImage(image));
        }

        if (imageMain.current?.options?.looping) {
            runAnim(0);
        } else {
            clearTimeout(loopingRef.current);
            loopingRef.current = undefined;

        }

    }, [image]);

    useEffect(() => {
        // runAnim(0);
    }, [triggerLooping]);

    const setImage = (image: TimageINT) => {
        const _image = { ...image, ...ImageClass._globalImages.get(image.key) };
        if (!_image.poster && !_image.url) { return; }
        if (_image.poster && !image.partialsToDisplay?.length) {
            previousImage.current = _image;
            return setImageObject({ url: _image.poster.url, partials: [] });
        }

        const autoAddImage = returnAutoCompleteImages(_image);
        if (_image.partialsToDisplay || (autoAddImage && autoAddImage?.length > 0)) {
            if (autoAddImage?.length) {
                if (!imageMain.current?.partialsToDisplay && !imageMain.current?.partial) { tempPartials.length = 0; }
            }
            autoAddImage?.forEach(autoImage => {
                addToTempImages(tempPartials, _image.partials?.[autoImage]!);
            });
            _image.partialsToDisplay?.forEach(partial => {
                const partialsAutoAdd = setImage(partial);
                addToTempImages(tempPartials, { ...ImageClass._globalImages.get(partial.key), ...partial });
                partialsAutoAdd?.forEach(autoImage => {
                    addToTempImages(tempPartials, partial.partials?.[autoImage]!);
                });
            });

        }

        setImageObject({ url: imageMain.current!.url, partials: tempPartials });
        previousImage.current = _image as TimageINT;
        return autoAddImage;
    };
    const getCurrentImage = () => {
        return imageMain;
    };
    const runAnim = (index: number) => {
        const currentImage = store.getState().gameObject.present.image;
        if (loopingRef.current) { return; }
        // console.log("index", index, console.timeStamp);
        loopingRef.current = setTimeout(() => {
            if (index >= Object.entries(image.loopingImages!).length) {
                setImage(currentImage);
                // setImageObject({ url: currentImage.url, partials: [] });

            } else {

                setImage(Object.entries(image.loopingImages!)[index][1]);
                // setImageObject({ url: Object.entries(image.loopingImages!)[index][1].url, partials: [] });
            }
            if (index >= Object.entries(image.loopingImages!).length) {
                if (currentImage?.options?.looping) {
                    clearTimeout(loopingRef.current);
                    loopingRef.current = undefined;
                    return runAnim(0);
                }

            } else {
                if (currentImage?.options?.looping) {
                    clearTimeout(loopingRef.current);
                    loopingRef.current = undefined;
                    return runAnim(index + 1);
                }

            }

        }, currentImage.options?.looping);

    };

    const addToTempImages = (tempPartials: React.ReactElement<any, string | React.JSXElementConstructor<any>>[], image: TimageINT) => {
        if (!image) { return; }

        if (image.options?.putParent && image.parent && imageObject.url !== image.url) {
            if (imageMain.current) {
                imageMain.current = { ...imageMain.current, options: {} };
            }
            addToTempImages(tempPartials, image.parent);
            returnAutoCompleteImages(image.parent)?.forEach(autoImage => {
                addToTempImages(tempPartials, image.parent?.partials?.[autoImage]!);
            });
        }
        const imageExists = tempPartials.findIndex(_image => _image.key == image.key);
        if (imageExists >= 0) {
            tempPartials.splice(imageExists, 1);
        }
        tempPartials.push(returnPartialElement(image));

    };
    const returnAutoCompleteImages = (_image: TimageINT | undefined) => {
        if (!_image) { return; }
        const autoAddImage = _image.partials && Object.keys(_image.partials).filter(part => {
            if (_image.partials?.[part].url && typeof _image.partials[part]?.options?.autoAdd === 'function') {
                return _image.partials[part]?.options?.autoAdd?.();

            } else if (_image.partials?.[part].url && ImageClass._globalImages.get(_image.partials?.[part].key)?.options?.autoAdd) {
                return true;
            }
            return false;
        });
        autoAddImage?.forEach(par => {
            if (image.partials?.[par]) {
                store.dispatch(setSeenImage(image.partials?.[par]));
            }
        });

        return autoAddImage;

    };

    const returnPartialElement = (partial: TimageINT) => {
        return <Image key={partial.key} source={partial?.url} style={[styles.image,]} resizeMode={'cover'} ></Image >;
    };
    const styles = StyleSheet.create({
        image: {
            position: "absolute",
            width: "100%",
            height: "100%",
            opacity: 1,
            flex: 1,


        }
    });
    return (
        <>
            {process.env.NODE_ENV === "development" ?
                <View style={{ position: "absolute", height: "5%", width: "100%", zIndex: 1, top: 25 }}>
                    <Text onPress={(e) => {
                        if (!isMobile()) {
                            const str = (e.currentTarget as any)?.innerText;
                            const splitStr = str.split("/");
                            const fileName = splitStr[splitStr.length - 1].split(".")[0];
                            navigator.clipboard.writeText(fileName);
                        }
                    }} selectable={true} style={{ color: "white", textAlign: "center" }}>
                        {(imageObject?.url?.toString().length || 0) > 100 ? '' : imageObject?.url}
                    </Text>
                    {imageObject.partials.map(p => {
                        return (
                            <Text onPress={(e) => {
                                if (!isMobile()) {
                                    const str = (e.currentTarget as any)?.innerText;
                                    const splitStr = str.split("/");
                                    const fileName = splitStr[splitStr.length - 1].split(".")[0];
                                    navigator.clipboard.writeText(fileName);
                                }
                            }} selectable={true} key={p.key} style={{ color: "white", textAlign: "center" }}>
                                {p.props.source}
                            </Text>
                        );
                    })}
                </View>
                : <></>}

            {imageObject?.url ? <WaitForLoadImage
                onFinishedPainting={(image) => {
                    onFinishedPainting?.(image);
                }}
                source={imageObject?.url}
                options={imageMain.current?.partialsToDisplay?.[imageMain.current.partialsToDisplay?.length - 1] ? (imageMain.current?.partialsToDisplay?.[imageMain.current.partialsToDisplay?.length - 1]?.options || {}) : imageMain.current?.options}
                partials={imageObject.partials}
                style={[styles.image,]}
                resizeMode={'contain'}
            >
            </WaitForLoadImage> : <></>}
            {/* <Image style={styles.image} source={parseImage(require("@lss/common/game/assets/images/V6/VanessaHouse/VanessasRoomEvent_4_p.webp"))}></Image> */}
            {/* {imageToDisplayArr.map(image => (image))} */}
        </>

    );
});

